import {
  type OfferUI,
  type ProductsSequence,
  type ProductsSequenceProduct
} from 'common/contracts';
import { EProgressBarType } from 'constants/enums';
import * as yup from 'yup';

import { MAX_PRODUCTS_PER_MILESTONE } from './ProgressBars.consts';

export const isMilestoneProgressBarDesign = (offerUI?: OfferUI): boolean =>
  offerUI?.progressBar?.type === EProgressBarType.MILESTONE;

const mapSequenceProducts = (products: unknown): ProductsSequenceProduct[] => {
  if (!Array.isArray(products)) return [];

  return products
    .filter((product: any) => product)
    .map((product: any) => ({
      productId: product.productId || product.product?.productId,
      quantity: product.quantity,
      priority: product.priority
    }));
};

export const mapProductsSequenceToMissions = (
  productsSequence?: ProductsSequence[]
): ProductsSequence[] => {
  if (!productsSequence?.length) {
    return [{ index: 1, products: [], priceInUsdCents: 0, barSize: 1 }];
  }

  return productsSequence.map((item: any) => ({
    index: item.index,
    priceInUsdCents:
      typeof item.priceInUsdCents === 'number' ? item.priceInUsdCents : 0,
    barSize: typeof item.barSize === 'number' ? item.barSize : 1,
    products: mapSequenceProducts(item?.products)
  }));
};

export const mapMissionsToProductsSequencePayload = (
  productsSequence: ProductsSequence[]
): ProductsSequence[] =>
  productsSequence.map((sequence) => ({
    ...sequence,
    products: (sequence?.products || []).map(
      (product: ProductsSequenceProduct) => ({
        productId: product.productId,
        quantity: product.quantity,
        priority: product.priority
      })
    )
  }));

const buildProductSchema = (): yup.AnyObjectSchema =>
  yup.object().shape({
    productId: yup.string().required('Product is required'),
    quantity: yup
      .number()
      .typeError('Quantity must be a number')
      .required('Quantity is required')
      .min(1, 'Quantity must be at least 1')
  });

const buildMilestoneProductsSchema = (): yup.AnySchema =>
  yup
    .array()
    .of(buildProductSchema())
    .min(1, 'At least one product is required')
    .max(
      MAX_PRODUCTS_PER_MILESTONE,
      `Up to ${MAX_PRODUCTS_PER_MILESTONE} products allowed`
    );

const buildMissionProductsSchema = (): yup.AnySchema =>
  yup
    .array()
    .of(buildProductSchema())
    .test('valid-product', 'Product must have a quantity', (missionProducts) => {
      if (!missionProducts || missionProducts.length === 0) return true;
      return (
        missionProducts[0]?.quantity !== undefined &&
        missionProducts[0]?.quantity !== null
      );
    });

export const getProductsSequenceValidationSchema = (
  isMilestoneDesign: boolean
): yup.AnySchema =>
  yup.array().of(
    yup.object().shape({
      index: yup.number().required(),
      products: isMilestoneDesign
        ? buildMilestoneProductsSchema()
        : buildMissionProductsSchema()
    })
  );
